// Broad "looks like an audio file" check (matches the resolver IPC's set).
const AUDIO_FILE_RE = /\.(mp3|wav|wave|aif|aiff|flac|m4a|ogg|aac|alac)$/i;
// The compat/loudness BACKEND walkers process exactly this set
// (src/backend/utils/index.js isAudioFile) — keep in sync. Pre-populated rows
// must match what the run will actually report on, or rows pulse forever
// (walker skips them) or results prepend mid-run (walker adds them).
const WALKER_EXT_RE = /\.(wav|aiff|mp3|aac|flac|alac|m4a)$/i;

const parentDir = (p) => {
    const norm = p.replace(/\\/g, '/');
    return norm.slice(0, norm.lastIndexOf('/'));
};

// The ordered list of audio files an Analyze run will process, resolved
// BEFORE the run starts so the tab can pre-populate its rows in a stable
// order (results then update rows in place instead of reordering the list).
//
// Explicit file paths keep their given order — playlist position, Library
// sort, or the pick order — even when directories ride along in the same
// selection (one odd extension must not reroute a playlist through the
// folder path and A-Z it). Only DIRECTORIES are expanded via the
// recursive-walk IPC and sorted by `labelFor`, the displayed
// "Artist - Title" name. Everything is filtered to the backend walkers'
// extension set so the pre-populated rows exactly match the run.
export async function resolveOrderedAudioFiles(paths, { includeSubDirs = true, labelFor = null } = {}) {
    if (!Array.isArray(paths) || paths.length === 0) return [];
    const explicit = paths.filter((p) => AUDIO_FILE_RE.test(p)).filter((p) => WALKER_EXT_RE.test(p));
    const dirs = paths.filter((p) => !AUDIO_FILE_RE.test(p));
    if (dirs.length === 0) return explicit;
    const expanded = await window.electron?.cuePoints?.resolveAudioFiles?.(dirs);
    if (!Array.isArray(expanded)) return explicit;
    let files = expanded.filter((f) => WALKER_EXT_RE.test(f));
    if (!includeSubDirs) {
        const dirSet = new Set(dirs.map((d) => d.replace(/\\/g, '/').replace(/\/+$/, '')));
        files = files.filter((f) => dirSet.has(parentDir(f)));
    }
    if (typeof labelFor === 'function') {
        files = [...files].sort((a, b) =>
            String(labelFor(a) || '').localeCompare(String(labelFor(b) || ''), undefined, { sensitivity: 'base', numeric: true }));
    }
    // Mixed selection: explicit files first (their given order), then the
    // expanded folders.
    const seen = new Set(explicit);
    return [...explicit, ...files.filter((f) => !seen.has(f))];
}
